// ECR / ECN change control — the only route to changing released data.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { D, designError, fmtDate, fmtDateTime, label, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, FilesPanel, NoAccess, PageHeader, PriorityTag, Section, SignDialog, SimpleSelect,
  StatusBadge, WorkflowBar,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, GitBranch, Plus, RefreshCw, Trash2, Ban } from "lucide-react";
import { toast } from "sonner";

const ENTITY_OPTS = [
  { value: "items", label: "Item" }, { value: "documents", label: "Drawing / Doc" }, { value: "boms", label: "BOM" },
  { value: "routings", label: "Routing" }, { value: "standards", label: "Standard" },
];
const ROUTE = { items: "items", documents: "documents", boms: "boms", routings: "routings", standards: "standards" };

function AffectedEditor({ value, onChange, withAction }) {
  const { me } = useDesignMe();
  const [opts, setOpts] = useState({});
  const row = { entity: "items", number: "", action: "revise", disposition: "not_applicable", remarks: "" };
  const lookup = (entity, q) => api.get(`${D}/${entity}`, { params: { q, status: "released" } })
    .then((r) => setOpts((o) => ({ ...o, [entity]: r.data.slice(0, 30) }))).catch(() => {});
  const upd = (i, k, v) => onChange(value.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <div className="space-y-2" data-testid="affected-editor">
      {value.map((a, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-2"><SimpleSelect value={a.entity} onChange={(v) => upd(i, "entity", v)} options={ENTITY_OPTS} /></div>
          <div className="col-span-3">
            <Input list={`aff-${i}`} placeholder="Released number" value={a.number}
              onFocus={() => lookup(a.entity, a.number)}
              onChange={(e) => { upd(i, "number", e.target.value.toUpperCase()); lookup(a.entity, e.target.value); }} />
            <datalist id={`aff-${i}`}>{(opts[a.entity] || []).map((o) => <option key={o.id} value={o.number}>{o.name || o.title || o.item_name}</option>)}</datalist>
          </div>
          {withAction && <div className="col-span-2"><SimpleSelect value={a.action} onChange={(v) => upd(i, "action", v)} options={me?.enums?.ecn_actions || ["revise", "obsolete"]} /></div>}
          {withAction && <div className="col-span-2"><SimpleSelect value={a.disposition} onChange={(v) => upd(i, "disposition", v)} options={me?.enums?.ecn_dispositions || []} /></div>}
          <div className={withAction ? "col-span-2" : "col-span-6"}><Input placeholder="Remarks" value={a.remarks || ""} onChange={(e) => upd(i, "remarks", e.target.value)} /></div>
          <button className="col-span-1 text-slate-400 hover:text-red-600 pb-2" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange([...value, row])}><Plus className="w-3.5 h-3.5 mr-1" />Add affected record</Button>
    </div>
  );
}

function Impact({ impact, onRefresh }) {
  if (!impact) return <div className="text-sm text-slate-500">No impact computed yet.</div>;
  const t = impact.totals;
  const block = (title, rows, cols) => (
    <Section title={`${title} (${rows.length})`}>
      {!rows.length ? <div className="text-xs text-slate-500">None.</div> : (
        <table className="w-full text-sm"><thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b">{cols.map(([k, h]) => <th key={k} className="py-1">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{cols.map(([k]) => <td key={k} className="py-1 mono text-xs">{k === "expected" ? fmtDate(r[k]) : String(r[k] ?? "")}</td>)}</tr>)}</tbody></table>
      )}
    </Section>
  );
  return (
    <div className="space-y-3" data-testid="ecn-impact">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        Snapshot {fmtDateTime(impact.computed_at)} · read live from Stores, Production and Purchase
        {onRefresh && <Button size="sm" variant="outline" onClick={onRefresh}><RefreshCw className="w-3.5 h-3.5 mr-1" />Recompute</Button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Stock on hand", t.stock_qty], ["WIP jobs", t.wip_jobs], ["Open work orders", t.open_work_orders], ["Open PO lines", t.open_po_lines]].map(([h, v]) => (
          <div key={h} className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{h}</div>
            <div className={`text-xl font-extrabold ${v ? "text-amber-600" : "text-slate-900"}`}>{v}</div></div>
        ))}
      </div>
      <div className="text-xs text-slate-600">Directly affected: <b className="mono">{impact.direct_parts.join(", ") || "—"}</b>
        {impact.parent_parts.length > 0 && <> · Parent assemblies (where-used): <b className="mono">{impact.parent_parts.join(", ")}</b></>}
        {impact.not_in_erp.length > 0 && <> · Not yet in ERP: <span className="mono">{impact.not_in_erp.join(", ")}</span></>}</div>
      {block("Stock", impact.stock, [["part_no", "Part"], ["warehouse", "Store"], ["qty", "Qty"]])}
      {block("Work in progress", impact.wip, [["part_no", "Part"], ["job", "Job"], ["stage", "Stage"], ["qty", "Qty"], ["batch_no", "Batch"]])}
      {block("Open work orders", impact.work_orders, [["part_no", "Part"], ["work_order", "WO"], ["status", "Status"], ["qty", "Qty"], ["sales_order", "TO"]])}
      {block("Open purchase orders", impact.purchase_orders, [["part_no", "Part"], ["po", "PO"], ["supplier", "Supplier"], ["qty", "Qty"], ["expected", "Expected"]])}
    </div>
  );
}

function EcnDetail({ id, onChanged }) {
  const { me, can } = useDesignMe();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [implementing, setImplementing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const load = useCallback(() => api.get(`${D}/ecns/${id}`).then((r) => { setRec(r.data); setForm(r.data); }).catch((e) => toast.error(designError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!rec) return null;
  const editable = rec.status === "draft" && can("ecn_create");
  const changed = () => { load(); onChanged?.(); };
  const save = async () => {
    setErrors([]);
    try {
      await api.patch(`${D}/ecns/${id}`, {
        title: form.title, description: form.description, reason: form.reason, priority: form.priority,
        effectivity: form.effectivity, effectivity_date: form.effectivity_date || null,
        affected: form.affected.filter((a) => a.number).map(({ entity, number, action, disposition, remarks }) => ({ entity, number, action, disposition, remarks })),
      });
      toast.success("Saved");
      changed();
    } catch (e) { setErrors([designError(e)]); }
  };
  const reviseAll = async () => {
    try {
      const { data } = await api.post(`${D}/ecns/${id}/revise-all`);
      toast.success(`${data.created.length} draft revision(s) opened${data.skipped.length ? ` · skipped: ${data.skipped.join("; ")}` : ""}`);
      changed();
    } catch (e) { toast.error(designError(e)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number}{rec.ecr_id ? " · from ECR" : ""}</div>
        <div className="text-xl font-bold text-slate-900">{rec.title}</div>
      </div>
      <WorkflowBar entity="ecns" record={{ ...rec, revision: "" }} onChanged={changed} extra={<>
        {rec.status === "approved" && can("create_edit") && <Button size="sm" variant="outline" onClick={reviseAll} data-testid="ecn-revise-all"><GitBranch className="w-3.5 h-3.5 mr-1" />Open new revisions</Button>}
        {rec.status === "approved" && can("release") && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setImplementing(true)} data-testid="ecn-implement"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Implement</Button>}
        {["draft", "approved"].includes(rec.status) && can("ecn_create") && <Button size="sm" variant="outline" className="text-red-600" onClick={() => setCancelling(true)}><Ban className="w-3.5 h-3.5 mr-1" />Cancel ECN</Button>}
      </>} />
      <Tabs defaultValue="affected">
        <TabsList>
          <TabsTrigger value="affected">Affected ({rec.affected?.length || 0})</TabsTrigger>
          <TabsTrigger value="impact">Impact</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="log">Signatures & log</TabsTrigger>
        </TabsList>
        <TabsContent value="affected" className="space-y-3">
          {editable ? <AffectedEditor value={form.affected || []} onChange={(v) => setForm((f) => ({ ...f, affected: v }))} withAction /> : (
            <table className="w-full text-sm"><thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b"><th className="py-1.5">Type</th><th>Number</th><th>Current</th><th>Action</th><th>Disposition</th><th>New revision</th><th>Done</th></tr></thead>
              <tbody>{rec.affected.map((a) => (
                <tr key={`${a.entity}-${a.number}`} className="border-b border-slate-50">
                  <td className="py-1.5">{ENTITY_OPTS.find((e) => e.value === a.entity)?.label}</td>
                  <td className="mono"><button className="hover:underline" onClick={() => nav(`/design/${ROUTE[a.entity]}?id=${a.current_id}`)}>{a.number}</button> <span className="text-xs text-slate-500">{a.title}</span></td>
                  <td className="mono">Rev {a.current_revision} <StatusBadge status={a.current_status} /></td>
                  <td className="uppercase text-xs font-semibold">{a.action}</td><td className="text-xs">{label(a.disposition)}</td>
                  <td>{a.new_revision ? <button className="hover:underline mono" onClick={() => nav(`/design/${ROUTE[a.entity]}?id=${a.new_revision.id}`)}>Rev {a.new_revision.revision} <StatusBadge status={a.new_revision.status} /></button> : "—"}</td>
                  <td>{a.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : ""}</td>
                </tr>
              ))}</tbody></table>
          )}
          <ErrorList errors={errors} />
          {editable && <div className="flex justify-end"><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="ecn-save">Save draft</Button></div>}
        </TabsContent>
        <TabsContent value="impact">
          <Impact impact={rec.impact} onRefresh={async () => { await api.post(`${D}/ecns/${id}/impact`); load(); }} />
        </TabsContent>
        <TabsContent value="details" className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title" className="sm:col-span-2"><Input value={form.title} disabled={!editable} onChange={set("title")} /></Field>
            <Field label="Priority"><SimpleSelect value={form.priority} disabled={!editable} onChange={set("priority")} options={me?.enums?.priorities || []} /></Field>
            <Field label="Effectivity"><SimpleSelect value={form.effectivity} disabled={!editable} onChange={set("effectivity")} options={["immediate", "date", "next_lot", "stock_exhaustion"]} /></Field>
            <Field label="Effectivity date"><Input type="date" value={form.effectivity_date || ""} disabled={!editable} onChange={set("effectivity_date")} /></Field>
            <Field label="Reason" className="sm:col-span-2"><Textarea rows={2} value={form.reason} disabled={!editable} onChange={set("reason")} /></Field>
            <Field label="Description of change" className="sm:col-span-2"><Textarea rows={4} value={form.description} disabled={!editable} onChange={set("description")} /></Field>
          </div>
          {editable && <div className="flex justify-end"><Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>Save draft</Button></div>}
        </TabsContent>
        <TabsContent value="files"><FilesPanel ownerType="ecns" ownerId={rec.id} files={rec.files} editable={editable} onChanged={load} /></TabsContent>
        <TabsContent value="log" className="space-y-2">
          {(rec.signatures || []).map((s) => <div key={s.id} className="text-sm"><b>{s.meaning}</b> — {s.user_name} &lt;{s.user_email}&gt; · {fmtDateTime(s.at)} {s.comment && `· ${s.comment}`}</div>)}
          {(rec.history || []).map((h, i) => <div key={i} className="text-xs text-slate-600">{fmtDateTime(h.at)} · {label(h.action)} · {h.by} {h.comment && `· ${h.comment}`}</div>)}
        </TabsContent>
      </Tabs>
      {implementing && <SignDialog open onOpenChange={setImplementing} title={`Implement ${rec.number}`}
        meaning="Confirms every new revision is released; records marked 'obsolete' are retired now."
        onSubmit={async ({ password, comment }) => { await api.post(`${D}/ecns/${id}/implement`, { password, comment }); toast.success("ECN implemented"); changed(); }} />}
      {cancelling && <SignDialog open onOpenChange={setCancelling} title={`Cancel ${rec.number}`} requireComment requirePassword={false}
        onSubmit={async ({ comment }) => { await api.post(`${D}/ecns/${id}/cancel`, { comment }); toast.success("ECN cancelled"); changed(); }} />}
    </div>
  );
}

function EcrDetail({ id, onChanged }) {
  const { can } = useDesignMe();
  const [rec, setRec] = useState(null);
  const [decide, setDecide] = useState(null);
  const load = useCallback(() => api.get(`${D}/ecrs/${id}`).then((r) => setRec(r.data)).catch((e) => toast.error(designError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!rec) return null;
  const changed = () => { load(); onChanged?.(); };
  const open = ["open", "under_review"].includes(rec.status);
  const convert = async () => {
    try { const { data } = await api.post(`${D}/ecrs/${id}/convert`); toast.success(`${data.number} created`); changed(); onChanged?.(data); } catch (e) { toast.error(designError(e)); }
  };
  return (
    <div className="space-y-4">
      <div><div className="text-xs text-slate-500 mono">{rec.number} · raised by {rec.raised_by_name || rec.raised_by} ({label(rec.raised_by_role)})</div>
        <div className="text-xl font-bold text-slate-900">{rec.title}</div></div>
      <div className="flex flex-wrap gap-2 items-center">
        <StatusBadge status={rec.status} /> <PriorityTag priority={rec.priority} />
        <div className="flex-1" />
        {open && can("ecr_review") && rec.status === "open" && <Button size="sm" variant="outline" onClick={() => setDecide("review")}>Start review</Button>}
        {open && can("ecr_review") && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setDecide("approve")} data-testid="ecr-approve">Approve</Button>}
        {open && can("ecr_review") && <Button size="sm" variant="outline" onClick={() => setDecide("reject")}>Reject</Button>}
        {open && <Button size="sm" variant="outline" onClick={() => setDecide("cancel")}>Cancel</Button>}
        {rec.status === "approved" && can("ecn_create") && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={convert} data-testid="ecr-convert">Convert to ECN</Button>}
      </div>
      <Section title="Request">
        <div className="text-sm whitespace-pre-wrap">{rec.description || "—"}</div>
        <div className="text-sm mt-2"><b>Reason:</b> {rec.reason || "—"}</div>
        <div className="text-sm mt-1"><b>Proposed change:</b> {rec.proposed_change || "—"}</div>
        <div className="text-sm mt-1"><b>Affected:</b> {(rec.affected || []).map((a) => `${a.number} (${a.entity})`).join(", ") || "—"}</div>
        {rec.ecn_number && <div className="text-sm mt-1"><b>ECN:</b> {rec.ecn_number}</div>}
      </Section>
      <FilesPanel ownerType="ecrs" ownerId={rec.id} files={rec.files} editable={open} onChanged={load} title="Attachments" />
      <Section title="History">{(rec.history || []).map((h, i) => <div key={i} className="text-xs text-slate-600">{fmtDateTime(h.at)} · {label(h.action)} · {h.by} {h.comment && `· ${h.comment}`}</div>)}</Section>
      {decide && <SignDialog open onOpenChange={(o) => !o && setDecide(null)} title={`${label(decide)} ${rec.number}`} requirePassword={false}
        requireComment={decide === "reject"} onSubmit={async ({ comment }) => { await api.post(`${D}/ecrs/${id}/decision`, { decision: decide, comment }); toast.success("Updated"); changed(); }} />}
    </div>
  );
}

export default function DesignChanges() {
  const { me, can, error: meErr } = useDesignMe();
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(params.get("ecr") ? "ecr" : "ecn");
  const [ecns, setEcns] = useState([]);
  const [ecrs, setEcrs] = useState([]);
  const [ecnId, setEcnId] = useState(params.get("ecn"));
  const [ecrId, setEcrId] = useState(params.get("ecr"));
  const [creating, setCreating] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", reason: "", priority: "medium", proposed_change: "", affected: [] });
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    api.get(`${D}/ecns`).then((r) => setEcns(r.data)).catch(() => setEcns([]));
    api.get(`${D}/ecrs`).then((r) => setEcrs(r.data)).catch(() => setEcrs([]));
  }, []);
  useEffect(load, [load]);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const create = async () => {
    setErrors([]);
    try {
      const body = { ...form, affected: form.affected.filter((a) => a.number) };
      if (creating === "ecr") {
        body.affected = body.affected.map(({ entity, number }) => ({ entity, number }));
        const { data } = await api.post(`${D}/ecrs`, body);
        toast.success(`${data.number} raised`);
        setEcrId(data.id); setTab("ecr");
      } else {
        delete body.proposed_change;
        const { data } = await api.post(`${D}/ecns`, body);
        toast.success(`${data.number} created`);
        setEcnId(data.id); setTab("ecn");
      }
      setCreating(null);
      setForm({ title: "", description: "", reason: "", priority: "medium", proposed_change: "", affected: [] });
      load();
    } catch (e) { setErrors([designError(e)]); }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-changes-page">
      <PageHeader title="Change Control" subtitle="ECR → ECN with impact on stock, WIP, purchase orders and work orders. Released data only changes here.">
        {can("ecr_create") && <Button variant="outline" onClick={() => setCreating("ecr")} data-testid="design-new-ecr"><Plus className="w-4 h-4 mr-1.5" />Raise ECR</Button>}
        {can("ecn_create") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating("ecn")} data-testid="design-new-ecn"><Plus className="w-4 h-4 mr-1.5" />New ECN</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="ecn">ECNs ({ecns.length})</TabsTrigger><TabsTrigger value="ecr">ECRs ({ecrs.length})</TabsTrigger></TabsList>
        <TabsContent value="ecn">
          <DataTable rows={ecns} onRowClick={(r) => setEcnId(r.id)} testid="design-ecn-table"
            columns={[
              { key: "number", title: "ECN", cellClass: "mono font-semibold" },
              { key: "title", title: "Title", cellClass: "max-w-sm truncate" },
              { key: "priority", title: "Priority", render: (r) => <PriorityTag priority={r.priority} /> },
              { key: "affected", title: "Affected", render: (r) => (r.affected || []).map((a) => a.number).join(", ") },
              { key: "impact", title: "Stock / WIP / WO / PO", render: (r) => { const t = r.impact?.totals; return t ? `${t.stock_qty} / ${t.wip_jobs} / ${t.open_work_orders} / ${t.open_po_lines}` : "—"; } },
              { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} stage={r.review_stage} /> },
              { key: "created_at", title: "Raised", render: (r) => fmtDate(r.created_at) },
            ]} />
        </TabsContent>
        <TabsContent value="ecr">
          <DataTable rows={ecrs} onRowClick={(r) => setEcrId(r.id)} testid="design-ecr-table"
            columns={[
              { key: "number", title: "ECR", cellClass: "mono font-semibold" },
              { key: "title", title: "Title", cellClass: "max-w-sm truncate" },
              { key: "priority", title: "Priority", render: (r) => <PriorityTag priority={r.priority} /> },
              { key: "raised_by", title: "Raised by" },
              { key: "raised_by_role", title: "Dept", render: (r) => label(r.raised_by_role) },
              { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
              { key: "created_at", title: "Raised", render: (r) => fmtDate(r.created_at) },
            ]} />
        </TabsContent>
      </Tabs>
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{creating === "ecr" ? "Raise Engineering Change Request" : "New Engineering Change Notice"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title *" className="sm:col-span-2"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="change-title" /></Field>
            <Field label="Priority"><SimpleSelect value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={me?.enums?.priorities || []} /></Field>
            <Field label="Reason"><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field>
            <Field label="Description" className="sm:col-span-2"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            {creating === "ecr" && <Field label="Proposed change" className="sm:col-span-2"><Textarea rows={2} value={form.proposed_change} onChange={(e) => setForm({ ...form, proposed_change: e.target.value })} /></Field>}
          </div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mt-2">Affected released records</div>
          <AffectedEditor value={form.affected} onChange={(v) => setForm({ ...form, affected: v })} withAction={creating === "ecn"} />
          <ErrorList errors={errors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={form.title.length < 3} onClick={create} data-testid="change-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!ecnId} onOpenChange={(o) => !o && setEcnId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto">
          <SheetHeader><SheetTitle>Engineering Change Notice</SheetTitle></SheetHeader>
          {ecnId && <EcnDetail key={ecnId} id={ecnId} onChanged={load} />}
        </SheetContent>
      </Sheet>
      <Sheet open={!!ecrId} onOpenChange={(o) => !o && setEcrId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader><SheetTitle>Engineering Change Request</SheetTitle></SheetHeader>
          {ecrId && <EcrDetail key={ecrId} id={ecrId} onChanged={(ecn) => { load(); if (ecn?.id) { setEcrId(null); setTab("ecn"); setEcnId(ecn.id); } }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
